<template>
    <div :class="driver" class="btn sso" @click="redirect">
        <img :src="logoSrc" class="logo" />
        {{
            $t("components.continue-with", {
                driver: capitalizeFirstLetter(driver),
            })
        }}
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, onBeforeUnmount } from "vue";
    import { Capacitor } from "@capacitor/core";
    import { ImpactStyle } from "@capacitor/haptics";
    import { safeHapticsImpact } from "@/utils/haptics";
    import { Browser } from "@capacitor/browser";
    import { onBeforeRouteLeave } from "vue-router";

    const props = defineProps({
        driver: {
            type: String,
            required: true,
        },
    });

    const logoImages: Record<string, string> = import.meta.glob(
        "@/assets/images/logo-*.svg",
        { eager: true, import: "default", query: "?url" },
    ) as Record<string, string>;

    const logoSrc = computed(() => {
        const key = `/src/assets/images/logo-${props.driver}.svg`;
        return logoImages[key] ?? "";
    });

    // Track if a browser window was opened by this component
    const browserOpened = ref(false);
    let browserFinishedListener: any = null;

    function capitalizeFirstLetter(inputString: string) {
        return inputString[0].toUpperCase() + inputString.slice(1);
    }

    onBeforeRouteLeave(async (to, from, next) => {
        // Only try to close browser if one was actually opened
        if (browserOpened.value) {
            try {
                await Browser.close();
                browserOpened.value = false;
            } catch (error) {
                // Silently handle errors (e.g., "No active window to close!")
                // This can happen if the browser was already closed by the user
                console.debug("Browser.close() error:", error);
                browserOpened.value = false;
            }
        }
        next();
    });

    async function redirect() {
        const redirectSchema = Capacitor.isNativePlatform()
            ? "sportspoule://"
            : import.meta.env.VUE_APP_BASE_URL;
        const redirectUrl = `${redirectSchema}/login`;
        safeHapticsImpact(ImpactStyle.Medium);
        const url = `${import.meta.env.VUE_APP_API_URL}/auth/redirect/${props.driver}?redirect_url=${redirectUrl}`;

        // Mark that we opened a browser window
        browserOpened.value = true;
        await Browser.open({ url });

        // Listen for browser close event to reset the flag
        browserFinishedListener = await Browser.addListener(
            "browserFinished",
            () => {
                browserOpened.value = false;
            },
        );
    }

    // Clean up listener when component is unmounted
    onBeforeUnmount(() => {
        if (browserFinishedListener) {
            browserFinishedListener.remove();
            browserFinishedListener = null;
        }
    });
</script>
